import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { http } from "@/lib/api";

const shortDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const exportCsv = (data, className) => {
  const header = ["Student", ...data.dates.flatMap((d) => [`${d} minutes`, `${d} status`])];
  const rows = data.rows.map((r) => [r.name, ...data.dates.flatMap((d) => [r.days[d], r.days[d] >= data.min_minutes ? "Present" : "Absent"])]);
  const csv = [header, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${className.replace(/\W+/g, "_")}_attendance.csv`;
  a.click();
};

export const AttendanceGrid = ({ classId, className }) => {
  const [data, setData] = useState(null);
  useEffect(() => { http.get(`/classes/${classId}/attendance`, { params: { days: 14 } }).then((r) => setData(r.data)); }, [classId]);
  if (!data) return <p className="text-stone-500">Loading attendance…</p>;

  return (
    <div data-testid="attendance-grid">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-stone-500">Active seat-time (minutes) in lessons, journal and tutor. <b className="text-[#2C4A3E]">Present</b> = at least {data.min_minutes} min.</p>
        <button onClick={() => exportCsv(data, className)} data-testid="attendance-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6DEC3] text-sm hover:bg-[#F1EADB] transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto rounded-3xl border border-[#E6DEC3] bg-white/60">
        <table className="text-sm min-w-full">
          <thead>
            <tr className="border-b border-[#E6DEC3] text-xs text-stone-500">
              <th className="px-4 py-3 text-left sticky left-0 bg-[#FBF8F2] font-semibold uppercase tracking-widest">Student</th>
              {data.dates.map((d) => <th key={d} className="px-2 py-3 font-medium whitespace-nowrap">{shortDate(d)}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.student_id} className="border-b border-[#E6DEC3]/60 last:border-0" data-testid={`attendance-row-${r.student_id}`}>
                <td className="px-4 py-3 font-medium text-[#2C4A3E] sticky left-0 bg-[#FBF8F2] whitespace-nowrap">{r.name}</td>
                {data.dates.map((d) => {
                  const m = r.days[d];
                  const present = m >= data.min_minutes;
                  return (
                    <td key={d} className="px-1.5 py-2 text-center">
                      <span title={`${m} min`} className={`inline-grid place-items-center w-10 h-8 rounded-lg text-xs ${present ? "bg-[#6B9080] text-white" : m > 0 ? "bg-[#F3DCC9] text-[#8a4b2a]" : "bg-[#F1EADB] text-stone-400"}`}>
                        {m > 0 ? Math.round(m) : "·"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={data.dates.length + 1} className="px-5 py-10 text-center text-stone-500">No students yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
